
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';

interface GalleryImageType {
  id?: number;
  src: string;
  alt: string;
  category: string;
  title?: string;
  description?: string;
  location?: string;
  tags?: string[];
  projectId?: string;
  isPrimary?: boolean;
}

interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  image: GalleryImageType | null;
}

export default function Lightbox({ isOpen, onClose, image }: LightboxProps) {
  const [projectImages, setProjectImages] = useState<GalleryImageType[]>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch all images for the project when lightbox opens
  useEffect(() => {
    const fetchProjectImages = async () => {
      if (!image || !isOpen) return;

      try {
        setIsLoading(true);
        const response = await fetch('/api/gallery');
        if (!response.ok) throw new Error('Failed to fetch images');

        const data = await response.json();
        if (data.success) {
          let images = data.data || [];
          
          // If this image has a projectId, get all images in the project
          if (image.projectId) {
            images = images.filter((img: GalleryImageType) => img.projectId === image.projectId);
            // Sort by primary first, then by id
            images.sort((a: GalleryImageType, b: GalleryImageType) => {
              if (a.isPrimary && !b.isPrimary) return -1;
              if (!a.isPrimary && b.isPrimary) return 1;
              return (a.id ?? 0) - (b.id ?? 0);
            });
          } else {
            // If no projectId, just show this single image
            images = [image];
          }

          setProjectImages(images);
          
          // Find the current image index
          const currentIndex = images.findIndex((img: GalleryImageType) => img.id != null && img.id === image.id);
          setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
        }
      } catch (error) {
        console.error('Error fetching project images:', error);
        setProjectImages([image]);
        setCurrentImageIndex(0);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectImages();
  }, [image, isOpen]);

  // Handle keyboard events (Escape to close, arrow keys for navigation)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, currentImageIndex, projectImages.length]);

  const goToNext = () => {
    if (projectImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % projectImages.length);
    }
  };

  const goToPrevious = () => {
    if (projectImages.length > 1) {
      setCurrentImageIndex((prev) => (prev - 1 + projectImages.length) % projectImages.length);
    }
  };

  const goToImage = (index: number) => {
    setCurrentImageIndex(index);
  };

  if (!isOpen || !image) return null;

  const currentImage = projectImages[currentImageIndex] || image;
  const hasMultipleImages = projectImages.length > 1;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div 
          className="fixed inset-0 z-50 flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {/* Backdrop */}
          <motion.div 
            className="absolute inset-0 bg-black/90"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Content container with max width */}
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-6 flex flex-col lg:flex-row gap-6 pointer-events-none">
            {/* Image and Navigation */}
            <motion.div 
              className="flex-1 pointer-events-auto"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <div className="relative bg-black rounded-xl overflow-hidden shadow-2xl">
                {/* Main Image */}
                <div className="relative aspect-video bg-black flex items-center justify-center min-h-[400px]">
                  {isLoading ? (
                    <div className="text-white">Loading project images...</div>
                  ) : (
                    <img
                      key={currentImage.id}
                      src={`/api/gallery/${currentImage.id}/image`}
                      alt={currentImage.title || currentImage.alt || 'Project image'}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTUwTDE1MCAyMDBIMjUwTDIwMCAxNTBaIiBmaWxsPSIjRDFEOUUwIi8+CjxjaXJjbGUgY3g9IjE3MCIgY3k9IjEyMCIgcj0iMjAiIGZpbGw9IiNEMUQ5RTAiLz4KPHRleHQgeD0iMjAwIiB5PSIyNjAiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY3NzI4MCIgdGV4dC1hbmNob3I9Im1pZGRsZSI+SW1hZ2UgTm90IEZvdW5kPC90ZXh0Pgo8L3N2Zz4K';
                      }}
                    />
                  )}
                </div>

                {/* Navigation Arrows */}
                {hasMultipleImages && (
                  <>
                    <button
                      onClick={goToPrevious}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={goToNext}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3 transition-all"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {hasMultipleImages && (
                  <div className="absolute top-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm">
                    {currentImageIndex + 1} / {projectImages.length}
                  </div>
                )}
              </div>

              {/* Thumbnail Navigation */}
              {hasMultipleImages && (
                <div className="mt-4 flex gap-2 justify-center overflow-x-auto pb-2">
                  {projectImages.map((img, index) => (
                    <button
                      key={img.id}
                      onClick={() => goToImage(index)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        index === currentImageIndex 
                          ? 'border-red-500 opacity-100' 
                          : 'border-gray-300 opacity-60 hover:opacity-80'
                      }`}
                    >
                      <img
                        src={`/api/gallery/${img.id}/image`}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0zMiAzMkwyNCA0MEg0MEwzMiAzMloiIGZpbGw9IiNEMUQ5RTAiLz4KPC9zdmc+Cg==';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Details Panel */}
            <motion.div 
              className="w-full lg:w-1/3 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-xl pointer-events-auto max-h-[80vh] overflow-y-auto"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {currentImage.title || currentImage.alt || 'Project Details'}
                  </h2>
                  <div className="mt-2">
                    <span className="inline-block bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300 text-xs px-2 py-1 rounded uppercase font-semibold tracking-wide">
                      {currentImage.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Location */}
              {currentImage.location && (
                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-4 text-sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>{currentImage.location}</span>
                </div>
              )}

              {/* Description */}
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Project Description</h3>
                <p className="text-gray-800 dark:text-gray-200">
                  {currentImage.description || `Professional ${currentImage.title || currentImage.alt || 'welding'} project showcasing our craftsmanship and attention to detail. This is one of our finest works that demonstrates our commitment to quality.`}
                </p>
              </div>

              {/* Project Stats */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">Project Type</h4>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {currentImage.category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                  </p>
                </div>
                <div className="bg-gray-100 dark:bg-gray-800 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">Images</h4>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {projectImages.length} photo{projectImages.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              

              {/* Project Tags */}
              {currentImage.tags && currentImage.tags.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Project Tags</h4>
                  <div className="flex flex-wrap gap-2">
                    {currentImage.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation Instructions */}
              {hasMultipleImages && (
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg mb-6">
                  <h4 className="text-xs font-medium text-blue-600 dark:text-blue-400 mb-1">Navigation</h4>
                  <p className="text-xs text-blue-600 dark:text-blue-300">
                    Use arrow keys or click thumbnails to browse all {projectImages.length} images
                  </p>
                </div>
              )}

              {/* Contact CTA */}
              <Button 
                className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
              >
                Inquire About Similar Projects
              </Button>
            </motion.div>
          </div>

          {/* Close button outside container (visible on mobile) */}
          <button
            className="absolute top-4 right-4 text-white bg-black/50 hover:bg-black/70 rounded-full p-2 lg:hidden"
            onClick={onClose}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
